import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
    ArrowLeft,
    Truck,
    ArrowRight,
    Calendar as CalendarIcon,
    PackageX,
    Phone,
    MapPin,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '-'; }
}

function formatMoveDate(yyyy_mm_dd) {
    if (!yyyy_mm_dd) return null;
    try {
        const d = new Date(yyyy_mm_dd + 'T00:00');
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return yyyy_mm_dd; }
}

export default function LivraisonsAFaire() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dismissingId, setDismissingId] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/dashboard/clients-a-livrer`, { withCredentials: true });
            setItems(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const dismiss = async (clientId, e) => {
        e?.stopPropagation?.();
        if (dismissingId) return;
        setDismissingId(clientId);
        try {
            await axios.post(`${API_URL}/api/clients/${clientId}/livraison/dismiss`, {}, { withCredentials: true });
            setItems((prev) => prev.filter((c) => c.id !== clientId));
            toast.success('Client retiré du bloc Livraison');
        } catch (err) {
            console.error(err);
            toast.error('Erreur lors du retrait');
        } finally {
            setDismissingId(null);
        }
    };

    return (
        <Layout>
            <div className="space-y-6" data-testid="livraisons-page">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/')}
                        className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm"
                        data-testid="livraisons-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <h1
                        className="text-2xl font-bold text-[#1B365D]"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                        Livraisons à programmer
                    </h1>
                </div>

                <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold text-[#1B365D] flex items-center gap-2">
                            <Truck className="w-5 h-5 text-orange-500" />
                            Clients à livrer
                            <Badge className="bg-orange-100 text-orange-700 border border-orange-200 text-xs ml-1">
                                {items.length}
                            </Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                    <Truck className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-gray-500 font-medium">Aucune livraison en attente</p>
                                <p className="text-gray-400 text-sm mt-1">
                                    Les clients acceptés apparaîtront ici jusqu'à ce que leur livraison soit programmée
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {items.map((c, i) => (
                                    <div
                                        key={c.id || i}
                                        onClick={() => navigate(`/clients/${c.id}`)}
                                        className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:border-orange-200 hover:bg-orange-50/30 cursor-pointer transition-all group"
                                        data-testid={`livraison-row-${i}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                                                <Truck className="w-3.5 h-3.5 text-orange-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-[#1B365D]">
                                                    {c.nom || '—'}
                                                </p>
                                                <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap mt-0.5">
                                                    {c.telephone ? (
                                                        <span className="flex items-center gap-1">
                                                            <Phone className="w-3 h-3" /> {c.telephone}
                                                        </span>
                                                    ) : null}
                                                    {c.date_demenagement ? (
                                                        <span className="flex items-center gap-1">
                                                            <CalendarIcon className="w-3 h-3" />
                                                            Démén. {formatMoveDate(c.date_demenagement)}
                                                        </span>
                                                    ) : null}
                                                    {c.created_at ? (
                                                        <span>Accepté le {formatDate(c.created_at)}</span>
                                                    ) : null}
                                                </div>
                                                {c.adresse_arrivee?.full || c.adresse_depart?.full ? (
                                                    <div className="mt-1 flex items-start gap-1 text-xs text-gray-500">
                                                        <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-gray-400" />
                                                        <span className="line-clamp-1">
                                                            {c.adresse_arrivee?.full || c.adresse_depart?.full}
                                                        </span>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={(e) => dismiss(c.id, e)}
                                                disabled={dismissingId === c.id}
                                                className="text-gray-600 hover:text-red-600 hover:border-red-300"
                                                data-testid={`livraison-dismiss-${i}`}
                                            >
                                                <PackageX className="w-4 h-4 mr-1.5" />
                                                Sans livraison
                                            </Button>
                                            <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-orange-500 transition-colors" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
